import { lazy } from 'react';
import { createBrowserRouter, Link, Navigate } from 'react-router';
import { Compass } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/layouts/AppShell';
import { Button, EmptyState } from '@/ui';
import { LoginPage } from '@/features/auth/LoginPage';
import { TechFormsPage } from '@/features/tech/TechFormsPage';
import { TechLoginPage } from '@/features/tech/TechLoginPage';
import { ProfilePage } from '@/features/auth/ProfilePage';
import { RequireAuth } from './guards';
import { FeaturePage } from './FeaturePage';
import { RouteError } from './RouteError';
import type { FeatureRoute } from './registry';
import { sections } from './sections';

const WelcomePage = lazy(() => import('@/features/home/WelcomePage'));

function NotFound() {
  const { t } = useTranslation('shell');
  return (
    <EmptyState
      size="page"
      icon={<Compass />}
      title={t('errors.notFound')}
      description={t('errors.notFoundDescription')}
      action={
        <Button asChild variant="primary">
          <Link to="/">{t('errors.goHome')}</Link>
        </Button>
      }
    />
  );
}

/** Former standalone paths, now a hub tab or a moved page: old links and bookmarks keep working. */
function legacyRedirects(r: FeatureRoute) {
  const moved = r.legacyPath ? [{ path: r.legacyPath, element: <Navigate to={r.path} replace /> }] : [];
  const tabs = (r.tabs ?? [])
    .filter((t) => t.legacyPath)
    .map((t) => ({
      path: t.legacyPath!,
      element: <Navigate to={`${r.path}?tab=${t.key}`} replace />,
    }));
  return [...moved, ...tabs];
}

const routes = sections.flatMap((s) => s.routes);

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
  // the VeoTech apps' "Form Access" (home.veoride.com/tech): technicians' own sign-in, not admins'
  { path: '/tech', element: <TechFormsPage />, errorElement: <RouteError /> },
  { path: '/tech/login', element: <TechLoginPage />, errorElement: <RouteError /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      // errors inside a screen keep the shell (menu, search) around the error page
      {
        errorElement: <RouteError />,
        children: [
          { index: true, element: <WelcomePage /> },
          { path: 'profile', element: <ProfilePage /> },
          ...routes.map((r) => ({
            path: r.path,
            element: <FeaturePage route={r} />,
          })),
          ...routes.flatMap(legacyRedirects),
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);
